import axios, { type AxiosResponse, type AxiosError } from 'axios'
import { jwtDecode } from 'jwt-decode'
import type {
  LoginRequest,
  RegisterRequest,
  VerifyEmailRequest,
  TokenResponse,
  User,
  InviteInfo,
  AcceptInviteResponse,
  RegisterResponse,
  UserUpdateProfile,
  UserUpdateAvatar,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse
} from '@/types/auth'
import type {
  Group,
  GroupCreate,
  GroupList,
  GroupMemberAdd,
  InviteLinkResponse,
  ParticipantAdd,
  FamilyAdd,
  FamilyUpdate,
  Family,
  UserSearchResult
} from '@/types/group'
import type {
  Transaction,
  TransactionCreate,
  TransactionUpdate,
  TransactionListItem,
  UserTransactionListItem,
  ParsedReceipt
} from '@/types/transaction'
import type {
  CreateSessionRequest,
  CreateSessionResponse,
  SessionInfo,
  ActiveSession
} from '@/types/collab'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Helper to check if token is expired or will expire soon
const isTokenExpiringSoon = (token: string, bufferSeconds: number = 120): boolean => {
  try {
    const decoded: any = jwtDecode(token)
    const expirationTime = decoded.exp * 1000 // Convert to milliseconds
    const currentTime = Date.now()
    const timeUntilExpiry = expirationTime - currentTime

    // Return true if token expires within buffer time (default 2 minutes)
    return timeUntilExpiry < bufferSeconds * 1000
  } catch (error) {
    return true // If we can't decode, assume expired
  }
}

// Flag to prevent multiple simultaneous refresh requests
let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

// Notify all subscribers when token is refreshed
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token))
  refreshSubscribers = []
}

// Add request to queue while token is refreshing
const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback)
}

// Request interceptor to add auth token and proactively refresh if needed
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('access_token')

    if (token && isTokenExpiringSoon(token)) {
      // Token is expiring soon, refresh it proactively
      if (!isRefreshing) {
        isRefreshing = true

        try {
          const refreshToken = localStorage.getItem('refresh_token')
          if (refreshToken) {
            const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
              refresh_token: refreshToken
            })

            const { access_token, refresh_token: new_refresh_token } = response.data
            localStorage.setItem('access_token', access_token)
            localStorage.setItem('refresh_token', new_refresh_token)

            isRefreshing = false
            onTokenRefreshed(access_token)

            config.headers.Authorization = `Bearer ${access_token}`
          }
        } catch (error) {
          isRefreshing = false
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          window.location.href = '/login'
          return Promise.reject(error)
        }
      } else {
        // Token is being refreshed, wait for it
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            config.headers.Authorization = `Bearer ${newToken}`
            resolve(config)
          })
        })
      }
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refresh_token')
        if (refreshToken) {
          // Direct axios call to avoid circular dependency
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
            refresh_token: refreshToken
          })
          
          const { access_token, refresh_token } = response.data
          localStorage.setItem('access_token', access_token)
          localStorage.setItem('refresh_token', refresh_token)
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// Auth API methods
export const authAPI = {
  // Register new user
  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    const response = await api.post('/api/auth/register', data)
    return response.data
  },

  // Verify email
  verifyEmail: async (data: VerifyEmailRequest): Promise<TokenResponse> => {
    const response = await api.post('/api/auth/verify-email', data)
    return response.data
  },

  // Login
  login: async (data: LoginRequest): Promise<TokenResponse> => {
    const response = await api.post('/api/auth/login', data)
    return response.data
  },

  // Get current user
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/api/auth/me')
    return response.data
  },

  // Logout
  logout: async (): Promise<{ message: string }> => {
    const response = await api.post('/api/auth/logout')
    return response.data
  },

  // Refresh token
  refreshToken: async (data: { refresh_token: string }): Promise<TokenResponse> => {
    const response = await api.post('/api/auth/refresh', data)
    return response.data
  },

  // Resend verification code
  resendVerificationCode: async (userId: string): Promise<{ message: string }> => {
    const response = await api.post('/api/auth/resend-verification-code', { user_id: userId })
    return response.data
  },

  // Update current user profile
  updateProfile: async (data: UserUpdateProfile): Promise<User> => {
    const response = await api.patch('/api/auth/me', data)
    return response.data
  },

  // Request password reset
  forgotPassword: async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    const response = await api.post('/api/auth/forgot-password', data)
    return response.data
  },

  // Reset password with token
  resetPassword: async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
    const response = await api.post('/api/auth/reset-password', data)
    return response.data
  },

  // Upload user avatar
  uploadAvatar: async (file: File): Promise<User> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/auth/me/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Delete user avatar
  deleteAvatar: async (): Promise<User> => {
    const response = await api.delete('/api/auth/me/avatar')
    return response.data
  },

  // Update user boring avatar
  updateBoringAvatar: async (avatarData: UserUpdateAvatar): Promise<User> => {
    const response = await api.put('/api/auth/me/avatar/boring', avatarData)
    return response.data
  }
}

// Invite API methods
export const inviteAPI = {
  // Get invite info
  getInviteInfo: async (token: string): Promise<InviteInfo> => {
    const response = await api.get(`/api/invites/${token}`)
    return response.data
  },

  // Validate invite token
  validateInvite: async (token: string): Promise<{ valid: boolean; reason?: string; message?: string; group?: any; expires_at?: string }> => {
    const response = await api.get(`/api/invites/${token}/validate`)
    return response.data
  },

  // Accept invite
  acceptInvite: async (token: string): Promise<AcceptInviteResponse> => {
    const response = await api.post(`/api/invites/${token}/accept`)
    return response.data
  }
}

// Group API methods
export const groupAPI = {
  // Create new group
  createGroup: async (data: GroupCreate): Promise<Group> => {
    const response = await api.post('/api/groups', data)
    return response.data
  },

  // Get user's groups
  getGroups: async (): Promise<GroupList[]> => {
    const response = await api.get('/api/groups')
    return response.data
  },

  // Get group details
  getGroup: async (groupId: string): Promise<Group> => {
    const response = await api.get(`/api/groups/${groupId}`)
    return response.data
  },

  // Update group
  updateGroup: async (groupId: string, data: Partial<{ name: string; avatar_type: string; avatar_data: any }>): Promise<Group> => {
    const response = await api.patch(`/api/groups/${groupId}`, data)
    return response.data
  },

  // Delete group
  deleteGroup: async (groupId: string): Promise<void> => {
    await api.delete(`/api/groups/${groupId}`)
  },

  // Add participants to group
  addParticipants: async (groupId: string, data: ParticipantAdd): Promise<Group> => {
    const response = await api.post(`/api/groups/${groupId}/participants`, data)
    return response.data
  },

  // Remove participant from group
  removeParticipant: async (groupId: string, participantId: string): Promise<void> => {
    await api.delete(`/api/groups/${groupId}/participants/${participantId}`)
  },

  // Search users for adding to group
  searchUsers: async (query: string, groupId?: string, limit: number = 5): Promise<UserSearchResult[]> => {
    const params: any = { q: query, limit }
    if (groupId) {
      params.group_id = groupId
    }
    const response = await api.get('/api/groups/users/search', { params })
    return response.data
  },

  // Family management
  createFamily: async (groupId: string, data: FamilyAdd): Promise<Family> => {
    const response = await api.post(`/api/groups/${groupId}/families`, data)
    return response.data
  },

  updateFamily: async (groupId: string, familyId: string, data: FamilyUpdate): Promise<Family> => {
    const response = await api.patch(`/api/groups/${groupId}/families/${familyId}`, data)
    return response.data
  },

  deleteFamily: async (groupId: string, familyId: string, deleteMembers: boolean = false): Promise<void> => {
    await api.delete(`/api/groups/${groupId}/families/${familyId}`, { params: { delete_members: deleteMembers } })
  },

  // Get invite link
  getInviteLink: async (groupId: string): Promise<InviteLinkResponse> => {
    const response = await api.get(`/api/groups/${groupId}/invite-link`)
    return response.data
  },

  // Send invite by email
  sendInviteByEmail: async (groupId: string, email: string): Promise<{ message: string }> => {
    const response = await api.post(`/api/groups/${groupId}/send-invite`, { email })
    return response.data
  },

  // Detect currency by IP
  detectCurrency: async (ip?: string): Promise<{ currency: string; country_name: string | null; ip: string }> => {
    const params = ip ? { params: { ip } } : {}
    const response = await api.get('/api/groups/detect-currency', params)
    return response.data
  },

  // Upload group image
  uploadGroupImage: async (groupId: string, file: File): Promise<Group> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post(`/api/groups/${groupId}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Delete group image
  deleteGroupImage: async (groupId: string): Promise<Group> => {
    const response = await api.delete(`/api/groups/${groupId}/image`)
    return response.data
  }
}

// Transaction API methods
export const transactionAPI = {
  // Create transaction
  createTransaction: async (groupId: string, data: TransactionCreate): Promise<Transaction> => {
    const response = await api.post(`/api/groups/${groupId}/transactions`, data)
    return response.data
  },

  // Get all transactions for a group
  getGroupTransactions: async (groupId: string, limit: number = 50, offset: number = 0): Promise<TransactionListItem[]> => {
    const response = await api.get(`/api/groups/${groupId}/transactions`, {
      params: { limit, offset }
    })
    return response.data
  },

  // Get all transactions for the current user across all groups
  getUserTransactions: async (limit: number = 50, offset: number = 0): Promise<UserTransactionListItem[]> => {
    const response = await api.get('/api/transactions', {
      params: { limit, offset }
    })
    return response.data
  },

  // Get transaction by ID
  getTransaction: async (groupId: string, transactionId: string): Promise<Transaction> => {
    const response = await api.get(`/api/groups/${groupId}/transactions/${transactionId}`)
    return response.data
  },

  // Update transaction
  updateTransaction: async (groupId: string, transactionId: string, data: TransactionUpdate): Promise<Transaction> => {
    const response = await api.patch(`/api/groups/${groupId}/transactions/${transactionId}`, data)
    return response.data
  },

  // Delete transaction
  deleteTransaction: async (groupId: string, transactionId: string): Promise<void> => {
    await api.delete(`/api/groups/${groupId}/transactions/${transactionId}`)
  }
}

// Receipt API
export const receiptAPI = {
  // Process receipt image with AI (OCR + GPT)
  processReceipt: async (imageFile: File): Promise<ParsedReceipt> => {
    const formData = new FormData()
    formData.append('file', imageFile)

    const response = await api.post('/api/receipts/process', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }
}

// Push notification API
export const pushAPI = {
  // Get VAPID public key
  getVapidPublicKey: async (): Promise<{ public_key: string }> => {
    const response = await api.get('/api/push/vapid-public-key')
    return response.data
  },

  // Subscribe to push notifications
  subscribe: async (subscription: PushSubscriptionJSON): Promise<{ id: string }> => {
    const response = await api.post('/api/push/subscribe', {
      endpoint: subscription.endpoint,
      keys: subscription.keys
    })
    return response.data
  },

  // Unsubscribe from push notifications
  unsubscribe: async (endpoint: string): Promise<void> => {
    await api.post('/api/push/unsubscribe', { endpoint })
  },

  // Send collab invite push notifications
  sendCollabInvite: async (sessionId: string): Promise<{ sent: number }> => {
    console.log('[pushAPI] sendCollabInvite called for session:', sessionId)
    try {
      const response = await api.post('/api/push/send/collab-invite', {
        session_id: sessionId
      })
      console.log('[pushAPI] sendCollabInvite response:', response.data)
      return response.data
    } catch (error: any) {
      console.error('[pushAPI] sendCollabInvite error:', error)
      console.error('[pushAPI] sendCollabInvite error response:', error.response?.data)
      throw error
    }
  },

  // Test push notification
  testPush: async (): Promise<{ sent: number }> => {
    const response = await api.get('/api/push/test')
    return response.data
  }
}

// Collaborative session API
export const collabAPI = {
  // Create a new collaborative session
  createSession: async (data: CreateSessionRequest): Promise<CreateSessionResponse> => {
    console.log('[collabAPI] createSession called with data:', data)
    try {
      const response = await api.post('/api/collab/sessions', data)
      console.log('[collabAPI] createSession response:', response.data)
      return response.data
    } catch (error: any) {
      console.error('[collabAPI] createSession error:', error)
      console.error('[collabAPI] createSession error response:', error.response?.data)
      throw error
    }
  },

  // Get session info
  getSessionInfo: async (sessionId: string): Promise<SessionInfo> => {
    console.log('[collabAPI] getSessionInfo called for session:', sessionId)
    const response = await api.get(`/api/collab/sessions/${sessionId}`)
    console.log('[collabAPI] getSessionInfo response:', response.data)
    return response.data
  },

  // End a collaborative session
  endSession: async (sessionId: string, transactionId?: string): Promise<void> => {
    console.log('[collabAPI] endSession called for session:', sessionId, 'transaction:', transactionId)
    const params = transactionId ? { transaction_id: transactionId } : {}
    await api.delete(`/api/collab/sessions/${sessionId}`, { params })
    console.log('[collabAPI] endSession completed')
  },

  // Get WebSocket URL for a session
  getWebSocketUrl: (sessionId: string): string => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const baseUrl = API_BASE_URL.replace(/^https?:/, wsProtocol)
    const token = localStorage.getItem('access_token')
    const url = `${baseUrl}/api/collab/ws/${sessionId}?token=${token}`
    console.log('[collabAPI] getWebSocketUrl:', url)
    return url
  },

  // Get active collaborative sessions for a group
  getGroupActiveSessions: async (groupId: string): Promise<ActiveSession[]> => {
    console.log('[collabAPI] getGroupActiveSessions called for group:', groupId)
    try {
      const response = await api.get(`/api/collab/groups/${groupId}/sessions`)
      console.log('[collabAPI] getGroupActiveSessions response:', response.data)
      return response.data.sessions
    } catch (error: any) {
      console.error('[collabAPI] getGroupActiveSessions error:', error)
      console.error('[collabAPI] getGroupActiveSessions error response:', error.response?.data)
      throw error
    }
  }
}

export default api

